// ExamResult.js - Web Version with Ant Design Icons (Fixed)
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./ExamResult.css";

// Icons - Using Ant Design (already installed in your project)
import {
  TrophyOutlined,
  StarOutlined,
  LikeOutlined,
  CheckCircleOutlined,
  BookOutlined,
  ExclamationCircleOutlined,
  RedoOutlined,
  ShareAltOutlined,
  EnvironmentOutlined,
  CalculatorOutlined,
  BulbOutlined,
  ExperimentOutlined,
  HistoryOutlined,
  GlobalOutlined,
  TranslationOutlined,
  CalendarOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
  FileDoneOutlined,
  CrownOutlined,
  BankOutlined, // Using BankOutlined instead of BalanceScaleOutlined
  HomeOutlined  // Using HomeOutlined instead of GraduationCapOutlined
} from '@ant-design/icons';

// Create axios instance for API calls
const createAxiosInstance = () => {
  return axios.create({
    baseURL: process.env.REACT_APP_API_URL || "https://api.example.com",
    timeout: 10000,
  });
};

const ExamResult = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resultDetails, setResultDetails] = useState(null);
  const [sections, setSections] = useState([]);
  
  // For web routing
  const navigate = useNavigate();
  const location = useLocation();
  const examData = location.state?.examData;
  const examId = examData.uid
  

  // Fetch exam result from API
  useEffect(() => {
    fetchExamResult();
  }, [examId]);

  // Function to fetch exam result from API
  const fetchExamResult = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // For web, we might use localStorage instead of AsyncStorage
      const accessToken = localStorage.getItem("accessToken");
      const storedDeviceId = localStorage.getItem('deviceId') || 'web-browser';
      const storedUserIp = localStorage.getItem('userIp') || '127.0.0.1';
      
      if (!accessToken) {
        throw new Error("Authentication required. Please login again.");
      }

      console.log('Fetching exam result for UID:', examId);
      
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `Assessment/get-assessment-result-by-uid/${examId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'UserDevice': storedDeviceId,
            'UserIPAddress': storedUserIp,
          },
        }
      );

      console.log('API Response:', JSON.stringify(response.data, null, 2));

      if (response.data && response.data.code === 200) {
        const resultData = response.data.data;
        
        // Process the data for display
        const processedData = processExamData(resultData);
        console.log('Processed Data:', JSON.stringify(processedData, null, 2));
        
        setResultDetails(processedData);
        
        // Set sections data for detailed view
        if (resultData.sections && Array.isArray(resultData.sections)) {
          setSections(resultData.sections);
        }
      } else {
        throw new Error(response.data?.message || 'Failed to fetch exam result');
      }

    } catch (error) {
      console.error('Error fetching exam result:', error);
      
      // If API fails, try to use the passed examData
      if (examData) {
        console.log('Using passed examData as fallback');
        const processedData = processExamData(examData);
        setResultDetails(processedData);
        
        if (examData.sections && Array.isArray(examData.sections)) {
          setSections(examData.sections);
        }
        
        setError(null);
      } else {
        setError(error.message || 'An error occurred while loading the exam result');
      }
    } finally {
      setLoading(false);
    }
  };

  // Process exam data for display
  const processExamData = (data) => {
    console.log('Processing exam data structure:', data);
    
    // Extract assessment data (it's nested under 'assessment' key)
    const assessmentData = data.assessment || data;
    const sectionsData = data.sections || [];
    
    // Calculate total score from sections
    let totalScoreFromSections = 0;
    let totalQuestionsFromSections = 0;
    
    if (sectionsData.length > 0) {
      sectionsData.forEach(section => {
        totalScoreFromSections += section.score || 0;
        totalQuestionsFromSections += section.questions?.length || 0;
      });
    }
    
    // Use data from assessment or fallback to section calculations
    const totalScore = assessmentData.total_score || totalScoreFromSections || 0;
    const totalQuestions = assessmentData.total_questions || totalQuestionsFromSections || 0;
    const percentage = totalQuestions > 0 ? Math.round((totalScore / totalQuestions) * 100) : 0;
    
    return {
      // Basic info from assessment
      id: assessmentData.uid || data.uid || examId,
      title: assessmentData.name || 'Exam Result',
      subject: assessmentData.name || 'General',
      classLevel: assessmentData.class_level_name || 'Not Specified',
      assessmentType: assessmentData.assessmentTypeName || assessmentData.assessment_type || 'Test',
      date: assessmentData.created_at || new Date().toISOString(),
      
      // Scores
      score: totalScore,
      totalQuestions: totalQuestions,
      correctAnswers: totalScore, // Assuming total_score is correct answers count
      percentage: percentage,
      
      // Time
      timeUsedSeconds: assessmentData.time_used_seconds || 0,
      
      // Additional data
      paperNo: assessmentData.paper_no || null,
      paperIndexNo: assessmentData.paper_index_no || null,
      year: assessmentData.year || new Date().getFullYear(),
      award: assessmentData.award || null,
      assessmentId: assessmentData.assessment_id || null,
      
      // Sections data
      sections: sectionsData,
      
      // Raw data for reference
      rawData: data,
    };
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0 secs';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds} secs`;
  };

  const calculatePercentage = () => {
    if (!resultDetails) return 0;
    return resultDetails.percentage;
  };

  const getPerformanceMessage = () => {
    const percentage = calculatePercentage();
    
    if (percentage >= 90) return { 
      message: "Excellent Performance! 🎉", 
      color: "#29d159ff",
      icon: <TrophyOutlined />
    };
    if (percentage >= 80) return { 
      message: "Outstanding! 🌟", 
      color: "#4CAF50",
      icon: <StarOutlined />
    };
    if (percentage >= 70) return { 
      message: "Great Job! 👍", 
      color: "#8BC34A",
      icon: <LikeOutlined />
    };
    if (percentage >= 60) return { 
      message: "Good Effort! 👏", 
      color: "#FFC107",
      icon: <CheckCircleOutlined />
    };
    if (percentage >= 50) return { 
      message: "You Passed! ✅", 
      color: "#FF9800",
      icon: <BookOutlined />
    };
    if (percentage >= 40) return { 
      message: "Needs Improvement", 
      color: "#FF5722",
      icon: <ExclamationCircleOutlined />
    };
    return { 
      message: "Keep Practicing! 💪", 
      color: "#F44336",
      icon: <RedoOutlined />
    };
  };

  const getGrade = () => {
    const percentage = calculatePercentage();
    
    if (percentage >= 90) return "A+";
    if (percentage >= 85) return "A";
    if (percentage >= 75) return "B+";
    if (percentage >= 70) return "B";
    if (percentage >= 60) return "C+";
    if (percentage >= 55) return "C";
    if (percentage >= 45) return "D+";
    if (percentage >= 40) return "D";
    return "F";
  };

  const getSubjectIcon = (subject) => {
    const subjectLower = subject?.toLowerCase() || '';
    if (subjectLower.includes('bio')) return <EnvironmentOutlined />;
    if (subjectLower.includes('math')) return <CalculatorOutlined />;
    if (subjectLower.includes('phy')) return <BulbOutlined />;
    if (subjectLower.includes('chem')) return <ExperimentOutlined />;
    if (subjectLower.includes('eng')) return <FileDoneOutlined />;
    if (subjectLower.includes('hist')) return <HistoryOutlined />;
    if (subjectLower.includes('geo')) return <GlobalOutlined />;
    if (subjectLower.includes('civic')) return <BankOutlined />; // Using BankOutlined for civic
    if (subjectLower.includes('kisw')) return <TranslationOutlined />;
    return <HomeOutlined />; // Using HomeOutlined as default
  };

  const getGradeColor = (grade) => {
    const gradeMap = {
      'A+': '#29d159ff',
      'A': '#4CAF50',
      'B+': '#FFC107',
      'B': '#FF9800',
      'C+': '#FF9800',
      'C': '#FF9800',
      'D+': '#FF5722',
      'D': '#FF5722',
      'F': '#F44336',
    };
    return gradeMap[grade] || '#666';
  };

  // Render a section breakdown
  const renderSectionBreakdown = () => {
    if (!sections || sections.length === 0) return null;

    return (
      <div className="sections-card">
        <h3 className="sections-title">Section Performance</h3>
        
        {sections.map((section, index) => {
          const sectionScore = section.score || 0;
          const sectionQuestions = section.questions?.length || 0;
          const sectionPercentage = sectionQuestions > 0 ? Math.round((sectionScore / sectionQuestions) * 100) : 0;
          
          return (
            <div key={index} className="section-item">
              <div className="section-header">
                <span className="section-name">{section.name || `Section ${index + 1}`}</span>
                <span className="section-score">
                  {sectionScore}/{sectionQuestions} ({sectionPercentage}%)
                </span>
              </div>
              
              <div className="section-progress-bar">
                <div 
                  className="section-progress-fill" 
                  style={{ 
                    width: `${sectionPercentage}%`,
                    backgroundColor: getPerformanceMessage().color
                  }}
                />
              </div>
              
              <div className="section-stats">
                <div className="stat-badge">
                  <CheckOutlined className="stat-icon" />
                  <span className="stat-badge-text">{sectionScore} correct</span>
                </div>
                <div className="stat-badge">
                  <CloseOutlined className="stat-icon incorrect" />
                  <span className="stat-badge-text">{sectionQuestions - sectionScore} incorrect</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render quick statistics
  const renderQuickStats = () => {
    if (!sections || sections.length === 0) return null;

    let totalCorrect = 0;
    let totalIncorrect = 0;
    let totalQuestions = 0;

    sections.forEach(section => {
      const sectionScore = section.score || 0;
      const sectionQuestions = section.questions?.length || 0;
      totalCorrect += sectionScore;
      totalIncorrect += (sectionQuestions - sectionScore);
      totalQuestions += sectionQuestions;
    });

    const accuracy = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;

    return (
      <div className="quick-stats-card">
        <h3 className="quick-stats-title">Quick Stats</h3>
        
        <div className="quick-stats-grid">
          <div className="quick-stat-item">
            <div className="quick-stat-number">{accuracy}%</div>
            <div className="quick-stat-label">Accuracy</div>
          </div>
          
          <div className="quick-stat-item">
            <div className="quick-stat-number" style={{ color: '#29d159ff' }}>
              {totalCorrect}
            </div>
            <div className="quick-stat-label">Correct</div>
          </div>
          
          <div className="quick-stat-item">
            <div className="quick-stat-number" style={{ color: '#ff6b6b' }}>
              {totalIncorrect}
            </div>
            <div className="quick-stat-label">Incorrect</div>
          </div>
          
          <div className="quick-stat-item">
            <div className="quick-stat-number">{sections.length}</div>
            <div className="quick-stat-label">Sections</div>
          </div>
        </div>
      </div>
    );
  };

  const getIncorrectQuestions = () => {
    if (!sections || sections.length === 0) return [];
    
    const incorrectQuestions = [];
    
    sections.forEach(section => {
      if (section.questions && Array.isArray(section.questions)) {
        section.questions.forEach(question => {
          if (question.isCorrect === false) {
            incorrectQuestions.push({
              ...question,
              sectionName: section.name,
              explanation: question.explanation || "No explanation available"
            });
          }
        });
      }
    });
    
    return incorrectQuestions;
  };

  const handleShareResult = () => {
    // Web share API
    if (navigator.share && resultDetails) {
      navigator.share({
        title: `My Exam Result: ${resultDetails.subject}`,
        text: `I scored ${resultDetails.score}/${resultDetails.totalQuestions} (${resultDetails.percentage}%) on my ${resultDetails.subject} exam!`,
        url: window.location.href,
      })
      .catch((error) => {
        console.log('Error sharing:', error);
        // Fallback: copy to clipboard
        const textToCopy = `I scored ${resultDetails.score}/${resultDetails.totalQuestions} (${resultDetails.percentage}%) on my ${resultDetails.subject} exam!`;
        navigator.clipboard.writeText(textToCopy).then(() => {
          alert('Result copied to clipboard!');
        });
      });
    } else {
      // Fallback for browsers without Web Share API
      const textToCopy = `I scored ${resultDetails.score}/${resultDetails.totalQuestions} (${resultDetails.percentage}%) on my ${resultDetails.subject} exam!`;
      navigator.clipboard.writeText(textToCopy).then(() => {
        alert('Result copied to clipboard!');
      });
    }
  };

const handleReviewAnswers = () => {
  const wrongQuestions = getIncorrectQuestions();
  console.log('Incorrect questions count:', wrongQuestions.length);
  
  if (wrongQuestions.length === 0) {
    alert("No incorrect answers to review!");
    return;
  }
  
  navigate('/review', { 
    state: { 
      wrongQuestions: wrongQuestions,
      examId: resultDetails.id,
      examTitle: resultDetails.title,
      totalQuestions: resultDetails.totalQuestions,
      score: resultDetails.score,
      percentage: resultDetails.percentage,
      allQuestions: sections,
      examData: resultDetails.rawData,
      subject: resultDetails.subject,  // Added this
      classLevel: resultDetails.classLevel  // Added this
    }
  });
};

  const handleRetakeExam = () => {
    navigate('/exam-system-start', { 
      state: { 
        examId: resultDetails.assessmentId || resultDetails.id,
        examData: resultDetails.rawData 
      }
    });
  };

  // Render loading state
  if (loading) {
    return (
      <div className="exam-result-container">
        <div className="loading-container">
          <LoadingOutlined className="spinner" />
          <p className="loading-text">Loading Exam Result...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error && !resultDetails) {
    return (
      <div className="exam-result-container">
        <div className="error-container">
          <ExclamationCircleOutlined className="error-icon" />
          <h2 className="error-title">Error Loading Result</h2>
          <p className="error-text">{error}</p>
          <div className="error-actions">
            <button className="retry-button" onClick={fetchExamResult}>
              Retry
            </button>
            <button className="back-button" onClick={() => navigate(-1)}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render empty state
  if (!resultDetails) {
    return (
      <div className="exam-result-container">
        <div className="empty-container">
          <QuestionCircleOutlined className="empty-icon" />
          <h2 className="empty-title">No Result Data</h2>
          <p className="empty-text">Unable to load exam result</p>
          <button className="back-button" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const percentage = calculatePercentage();
  const performance = getPerformanceMessage();
  const grade = getGrade();
  const gradeColor = getGradeColor(grade);
  const subjectIcon = getSubjectIcon(resultDetails.subject);

  return (
    <div className="exam-result-container">
      <header className="exam-result-header">
        <button className="back-button-header" onClick={() => navigate(-1)}>
          <ArrowLeftOutlined />
          <span>Back</span>
        </button>
        <h1 className="header-title">Exam Result</h1>
        <div className="header-placeholder"></div>
      </header>
      
      <main className="exam-result-content">
        {/* Main Result Card */}
        <div className="result-card">
          {/* Subject Icon */}
          <div className="subject-icon-container" style={{ backgroundColor: `${performance.color}20` }}>
            <span className="subject-icon">{subjectIcon}</span>
          </div>

          {/* Performance Message */}
          <div className="performance-container">
            <span className="performance-icon">{performance.icon}</span>
            <span className="performance-text" style={{ color: performance.color }}>
              {performance.message}
            </span>
          </div>

          {/* Score Circle */}
          <div className="score-container">
            <div className="score-circle" style={{ borderColor: performance.color }}>
              <div className="score-percentage">{percentage}%</div>
              <div className="score-label">Score</div>
            </div>
          </div>

          {/* Grade and Score Details */}
          <div className="grade-score-container">
            <div className="grade-item">
              <div className="grade-label">Grade</div>
              <div className="grade-text" style={{ color: gradeColor }}>
                {grade}
              </div>
            </div>
            
            <div className="score-divider"></div>
            
            <div className="score-detail-item">
              <div className="score-detail-label">Score</div>
              <div className="score-detail-value">
                {resultDetails.score} / {resultDetails.totalQuestions}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        {renderQuickStats()}

        {/* Exam Details */}
        <div className="details-card">
          <h3 className="details-title">Exam Details</h3>
          
          <div className="detail-grid">
            <div className="detail-item">
              <FileDoneOutlined className="detail-icon" />
              <div className="detail-label">Subject</div>
              <div className="detail-value">{resultDetails.subject}</div>
            </div>
            
            <div className="detail-item">
              <HomeOutlined className="detail-icon" />
              <div className="detail-label">Class</div>
              <div className="detail-value">{resultDetails.classLevel}</div>
            </div>
            
            <div className="detail-item">
              <FileTextOutlined className="detail-icon" />
              <div className="detail-label">Type</div>
              <div className="detail-value">{resultDetails.assessmentType}</div>
            </div>
            
            <div className="detail-item">
              <CalendarOutlined className="detail-icon" />
              <div className="detail-label">Date</div>
              <div className="detail-value">
                {new Date(resultDetails.date).toLocaleDateString('en-US', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Section Breakdown */}
        {renderSectionBreakdown()}

        {/* Score Breakdown */}
        <div className="breakdown-card">
          <div className="breakdown-header">
            <h3 className="breakdown-title">Overall Performance</h3>
            <div className="breakdown-percentage">{percentage}%</div>
          </div>
          
          <div className="score-bar-container">
            <div className="score-bar-background">
              <div 
                className="score-bar-fill" 
                style={{ 
                  width: `${percentage}%`,
                  backgroundColor: performance.color
                }}
              />
            </div>
            
            <div className="score-bar-labels">
              <span className="score-bar-label">0%</span>
              <span className="score-bar-label">50%</span>
              <span className="score-bar-label">100%</span>
            </div>
          </div>
          
          <div className="score-text-container">
            <p className="score-text">
              You scored {resultDetails.score} out of {resultDetails.totalQuestions} questions
            </p>
            <p className="time-text">
              <ClockCircleOutlined /> Time taken: {formatTime(resultDetails.timeUsedSeconds)}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button 
            className="action-button review-button" 
            onClick={handleReviewAnswers}
          >
            <SearchOutlined />
            <span className="review-button-text">
              Review Answers ({getIncorrectQuestions().length})
            </span>
          </button>
          
          <button 
            className="action-button retake-button"
            onClick={handleRetakeExam}
          >
            <RedoOutlined />
            <span className="retake-button-text">Retake Exam</span>
          </button>
          
          <button 
            className="action-button share-button"
            onClick={handleShareResult}
          >
            <ShareAltOutlined />
            <span className="share-button-text">Share</span>
          </button>
        </div>

        {/* Error Banner */}
        {error && resultDetails && (
          <div className="error-banner">
            <InfoCircleOutlined />
            <span className="error-banner-text">
              Showing cached result: {error}
            </span>
          </div>
        )}
      </main>
    </div>
  );
};

export default ExamResult;